/* battleFx — synthesized audio cues (Web Audio API) + haptic feedback for
 * the PvP battle experience. Every function is a no-op when the
 * browser doesn't support the corresponding capability so nothing
 * ever throws on older phones.
 *
 * Audio: single lazy-allocated AudioContext, short envelope-shaped
 * blips. No external files, no licensing, ~2kB gz.
 * Haptics: navigator.vibrate wrapper, respects `prefers-reduced-motion`.
 *
 * Public API:
 *   const fx = getFx({ soundOn, motionOk })
 *   fx.play('hit' | 'miss' | 'crit' | 'ability' | 'round_win' |
 *           'round_loss' | 'victory' | 'defeat' | 'intro')
 *   fx.hapt('light' | 'medium' | 'strong' | 'celebration')
 */

let _ctx = null;
function ctx() {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    try {
      const C = window.AudioContext || window.webkitAudioContext;
      if (!C) return null;
      _ctx = new C();
    } catch { return null; }
  }
  // Resume on user gesture: iOS Safari suspends contexts by default.
  if (_ctx.state === 'suspended') { _ctx.resume().catch(() => {}); }
  return _ctx;
}

// Single tone with linear-ramped envelope. type: 'sine' | 'square' | 'triangle' | 'sawtooth'.
function tone({ freq, type = 'sine', dur = 0.12, gain = 0.06, glide = 0 }) {
  const ac = ctx();
  if (!ac) return;
  try {
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ac.currentTime);
    if (glide) osc.frequency.exponentialRampToValueAtTime(Math.max(20, freq * (1 + glide)), ac.currentTime + dur);
    g.gain.setValueAtTime(0.0001, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(gain, ac.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
    osc.connect(g).connect(ac.destination);
    osc.start();
    osc.stop(ac.currentTime + dur + 0.02);
  } catch { /* silent */ }
}

// Filtered white noise burst — used for hits, misses.
function noise({ dur = 0.08, gain = 0.08, cutoff = 1200 }) {
  const ac = ctx();
  if (!ac) return;
  try {
    const buf = ac.createBuffer(1, Math.max(1, Math.floor(ac.sampleRate * dur)), ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const filt = ac.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = cutoff;
    const g = ac.createGain();
    g.gain.setValueAtTime(gain, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
    src.connect(filt).connect(g).connect(ac.destination);
    src.start();
    src.stop(ac.currentTime + dur + 0.02);
  } catch { /* silent */ }
}

// -----------------------------------------------------------------------
// Named cues — each is a tiny arrangement of tones/noise. Volumes are
// intentionally low so the cues sit under any ambient sound.
// -----------------------------------------------------------------------
const CUES = {
  intro:      () => { tone({ freq: 440, type: 'sine', dur: 0.25, gain: 0.05 });
                      setTimeout(() => tone({ freq: 660, type: 'triangle', dur: 0.35, gain: 0.06 }), 220); },
  hit:        () => { noise({ dur: 0.06, gain: 0.09, cutoff: 900 });
                      tone({ freq: 180, type: 'square', dur: 0.05, gain: 0.05, glide: -0.4 }); },
  miss:       () => { tone({ freq: 320, type: 'sine', dur: 0.08, gain: 0.03, glide: -0.5 }); },
  crit:       () => { noise({ dur: 0.09, gain: 0.12, cutoff: 2400 });
                      tone({ freq: 220, type: 'sawtooth', dur: 0.14, gain: 0.07, glide: -0.5 });
                      setTimeout(() => tone({ freq: 660, type: 'square', dur: 0.09, gain: 0.05 }), 60); },
  ability:    () => { tone({ freq: 520, type: 'triangle', dur: 0.18, gain: 0.05, glide: 0.4 });
                      setTimeout(() => tone({ freq: 780, type: 'triangle', dur: 0.14, gain: 0.05 }), 140); },
  round_win:  () => { tone({ freq: 523, type: 'triangle', dur: 0.14, gain: 0.06 });
                      setTimeout(() => tone({ freq: 659, type: 'triangle', dur: 0.16, gain: 0.06 }), 130); },
  round_loss: () => { tone({ freq: 330, type: 'sine', dur: 0.16, gain: 0.05, glide: -0.3 }); },
  victory:    () => { [523, 659, 784, 1046].forEach((f, i) => setTimeout(() => tone({ freq: f, type: 'triangle', dur: 0.25, gain: 0.06 }), i * 120)); },
  defeat:     () => { [440, 349, 262].forEach((f, i) => setTimeout(() => tone({ freq: f, type: 'sine', dur: 0.35, gain: 0.05 }), i * 220)); },
};

const HAPT_PATTERNS = {
  light: 15,
  medium: [20, 30, 20],
  strong: [45, 30, 45],
  celebration: [40, 40, 40, 40, 100, 40, 200],
};

export function getFx({ soundOn = true, motionOk = true } = {}) {
  return {
    play(cue) { if (!soundOn) return; const fn = CUES[cue]; if (fn) fn(); },
    hapt(pattern) {
      if (!motionOk) return;
      if (typeof navigator === 'undefined' || !navigator.vibrate) return;
      try { navigator.vibrate(HAPT_PATTERNS[pattern] || 15); } catch { /* silent */ }
    },
    // Called on the first tap so iOS Safari unlocks the AudioContext.
    unlock() { ctx(); },
  };
}
